import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="checkout-forms--multi-step"
//
// Splits the public checkout form into two visible steps so credit card
// fields render on a separate page from other personal information.
// Required by MA Gen. Laws ch. 93 § 105 which prohibits collecting credit
// card details on the same form as other personal identification.
//
// Submission semantics are unchanged: the form still POSTs once, from step 2.
export default class extends Controller {
  static values = { step: { type: Number, default: 1 } };
  static targets = ["submitButton"];
  static outlets = ["checkout-forms"];

  initialize() {
    // Capture the submit button's server-rendered disabled state before
    // stepValueChanged runs (it fires for the initial value before connect)
    // and overrides it as part of switching to step 1. This is the state we
    // restore on the payment step; back() keeps it in sync with any change a
    // payment controller makes (e.g. CardConnect enables the button once it
    // receives a valid token), so returning to payment doesn't clobber it.
    this.submitDisabledForPayment = this.hasSubmitButtonTarget && this.submitButtonTarget.disabled;
  }

  connect() {
    this.applyStep();
    this.element.addEventListener("keydown", this.handleKeydown);
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.handleKeydown);
  }

  next(event) {
    event?.preventDefault();
    // Only validate step-1 fields. Browsers do NOT bar `display:none` form
    // controls from constraint validation, so calling form.reportValidity()
    // would fail on empty step-2 fields like the credit card zip.
    const firstInvalid = this.firstInvalidStepOneField();
    if (firstInvalid) {
      firstInvalid.reportValidity();
      return;
    }
    // The phone-or-email requirement isn't enforced by native constraint
    // validation (neither field is individually `required`), so run that
    // check here. Both fields live on step 1, so a failure must keep the
    // shopper here rather than letting the error render next to hidden fields.
    if (this.hasCheckoutFormsOutlet && !this.checkoutFormsOutlet.validatePhoneOrEmail()) {
      return;
    }
    this.stepValue = 2;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  firstInvalidStepOneField() {
    const inputs = this.element.querySelectorAll(
      ".multi-step-1 input, .multi-step-1 select, .multi-step-1 textarea"
    );
    for (const el of inputs) {
      if (!el.willValidate) continue;
      if (!el.checkValidity()) return el;
    }
    return null;
  }

  back(event) {
    event?.preventDefault();
    // Preserve whatever disabled state the payment controller left the submit
    // button in (e.g. CardConnect enables it once a valid token arrives) so
    // returning to the payment step doesn't re-disable an already-valid form.
    if (this.hasSubmitButtonTarget) {
      this.submitDisabledForPayment = this.submitButtonTarget.disabled;
    }
    this.stepValue = 1;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  stepValueChanged() {
    this.applyStep();
  }

  applyStep() {
    this.setStepClass(this.stepValue);
    this.equalizeHeight();
    // Disable the real submit button on step 1 so pressing Enter in a
    // text field can't trigger implicit form submission.
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled =
        this.stepValue === 1 ? true : this.submitDisabledForPayment;
    }
  }

  setStepClass(step) {
    this.element.classList.remove("multi-step-current-1", "multi-step-current-2");
    this.element.classList.add(`multi-step-current-${step}`);
  }

  // Keep the form the same height on both steps so the page doesn't jump when
  // navigating between them: render each step (synchronously, between paints),
  // measure its natural height, and pin min-height to the taller one.
  // Re-measured on every step change so late-loading content (payment iframes,
  // recaptcha) is accounted for.
  equalizeHeight() {
    this.element.style.minHeight = "";
    const heights = [1, 2].map((step) => {
      this.setStepClass(step);
      return this.element.offsetHeight;
    });
    this.setStepClass(this.stepValue);
    this.element.style.minHeight = `${Math.max(...heights)}px`;
  }

  handleKeydown = (event) => {
    if (this.stepValue !== 1) return;
    if (event.key !== "Enter") return;
    const tag = event.target.tagName;
    if (tag === "TEXTAREA" || tag === "BUTTON" || tag === "A") return;
    event.preventDefault();
    this.next();
  };
}
